const INSTALLED = new WeakSet<HTMLSelectElement>();
let observer: MutationObserver | null = null;
let openPanel: HTMLElement | null = null;
let openSelect: HTMLSelectElement | null = null;

function normalize(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('fr');
}

function selectedLabel(select: HTMLSelectElement): string {
  const option = select.selectedOptions[0];
  const text = option?.textContent?.trim() ?? '';
  if (text) return text;
  const placeholder = select.getAttribute('data-placeholder');
  return placeholder || 'Sélectionner…';
}

function optionList(
  select: HTMLSelectElement,
): Array<{ value: string; label: string; disabled: boolean }> {
  return Array.from(select.options).map((option) => ({
    value: option.value,
    label: option.textContent?.trim() ?? option.value,
    disabled: option.disabled,
  }));
}

function closePanel(): void {
  openPanel?.remove();
  openPanel = null;
  openSelect = null;
  document.querySelectorAll('.nx-combo-trigger[aria-expanded="true"]').forEach((el) => {
    el.setAttribute('aria-expanded', 'false');
  });
}

function positionPanel(trigger: HTMLElement, panel: HTMLElement): void {
  const rect = trigger.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(Math.max(rect.width, 220), vw - 16);
  let left = rect.left;
  if (left + width > vw - 8) left = Math.max(8, vw - width - 8);
  const below = vh - rect.bottom;
  const openUp = below < 260 && rect.top > below;
  panel.style.left = `${left}px`;
  panel.style.width = `${width}px`;
  if (openUp) {
    panel.style.top = 'auto';
    panel.style.bottom = `${vh - rect.top + 6}px`;
    panel.style.maxHeight = `${Math.min(320, Math.max(160, rect.top - 16))}px`;
  } else {
    panel.style.bottom = 'auto';
    panel.style.top = `${rect.bottom + 6}px`;
    panel.style.maxHeight = `${Math.min(320, Math.max(160, below - 16))}px`;
  }
}

function choose(select: HTMLSelectElement, value: string, trigger: HTMLButtonElement): void {
  if (select.value !== value) {
    select.value = value;
    select.dispatchEvent(new Event('change', { bubbles: true }));
    select.dispatchEvent(new Event('input', { bubbles: true }));
  }
  trigger.textContent = selectedLabel(select);
  trigger.classList.toggle('is-placeholder', !select.value);
  closePanel();
  trigger.focus();
}

function openDropdown(select: HTMLSelectElement, trigger: HTMLButtonElement): void {
  closePanel();
  if (select.disabled) return;

  const panel = document.createElement('div');
  panel.className = 'nx-combo-panel';
  panel.setAttribute('role', 'presentation');

  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'nx-combo-search';
  search.placeholder = 'Filtrer…';
  search.setAttribute('aria-label', 'Filtrer les options');
  search.autocomplete = 'off';

  const list = document.createElement('ul');
  list.className = 'nx-combo-list';
  list.setAttribute('role', 'listbox');

  panel.append(search, list);
  document.body.appendChild(panel);
  openPanel = panel;
  openSelect = select;
  trigger.setAttribute('aria-expanded', 'true');
  positionPanel(trigger, panel);

  let activeIndex = -1;
  const visibleButtons = (): HTMLButtonElement[] =>
    Array.from(list.querySelectorAll<HTMLButtonElement>('[role="option"]'));

  const render = (): void => {
    const query = normalize(search.value.trim());
    const options = optionList(select).filter((option) => {
      if (option.disabled) return false;
      if (!query) return true;
      return normalize(`${option.label} ${option.value}`).includes(query);
    });
    list.innerHTML = '';
    if (options.length === 0) {
      const empty = document.createElement('li');
      empty.className = 'nx-combo-empty';
      empty.setAttribute('role', 'status');
      empty.textContent = 'Aucun résultat';
      list.appendChild(empty);
      activeIndex = -1;
      return;
    }
    options.forEach((option, index) => {
      const item = document.createElement('li');
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', option.value === select.value ? 'true' : 'false');
      btn.className = 'nx-combo-option';
      if (option.value === select.value) btn.classList.add('is-selected');
      btn.textContent = option.label || '—';
      btn.addEventListener('mousedown', (event) => event.preventDefault());
      btn.addEventListener('click', () => choose(select, option.value, trigger));
      btn.addEventListener('mouseenter', () => {
        activeIndex = index;
        highlight();
      });
      item.appendChild(btn);
      list.appendChild(item);
    });
    const selected = visibleButtons().findIndex((btn) => btn.classList.contains('is-selected'));
    activeIndex = selected >= 0 ? selected : 0;
    highlight();
  };

  const highlight = (): void => {
    visibleButtons().forEach((btn, index) => {
      btn.classList.toggle('is-active', index === activeIndex);
    });
    visibleButtons()[activeIndex]?.scrollIntoView({ block: 'nearest' });
  };

  search.addEventListener('input', render);
  search.addEventListener('keydown', (event) => {
    const buttons = visibleButtons();
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (buttons.length === 0) return;
      activeIndex = (activeIndex + 1) % buttons.length;
      highlight();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (buttons.length === 0) return;
      activeIndex = (activeIndex - 1 + buttons.length) % buttons.length;
      highlight();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      buttons[activeIndex]?.click();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      closePanel();
      trigger.focus();
    }
  });

  render();
  window.setTimeout(() => search.focus(), 0);
}

function wrapSelect(select: HTMLSelectElement): void {
  if (INSTALLED.has(select) || select.multiple || select.size > 1) return;
  if (select.dataset.nxPlain === '1' || select.closest('.nx-combo')) return;

  const combo = document.createElement('div');
  combo.className = 'nx-combo';
  select.parentNode?.insertBefore(combo, select);
  combo.appendChild(select);
  select.classList.add('nx-combo-native');
  select.dataset.nxCombo = '1';
  select.setAttribute('tabindex', '-1');
  select.setAttribute('aria-hidden', 'true');

  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'nx-combo-trigger';
  trigger.setAttribute('aria-haspopup', 'listbox');
  trigger.setAttribute('aria-expanded', 'false');
  const labelledBy = select.getAttribute('aria-label') || select.id;
  if (labelledBy) trigger.setAttribute('aria-label', labelledBy);
  trigger.disabled = select.disabled;
  trigger.textContent = selectedLabel(select);
  trigger.classList.toggle('is-placeholder', !select.value);
  combo.insertBefore(trigger, select);

  const sync = (): void => {
    trigger.disabled = select.disabled;
    trigger.textContent = selectedLabel(select);
    trigger.classList.toggle('is-placeholder', !select.value);
  };

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    if (openSelect === select) {
      closePanel();
      return;
    }
    openDropdown(select, trigger);
  });

  select.addEventListener('change', sync);
  const optionsWatch = new MutationObserver(sync);
  optionsWatch.observe(select, { childList: true, subtree: true, attributes: true });
  INSTALLED.add(select);
}

export function enhanceSearchableSelects(root: ParentNode = document): void {
  root.querySelectorAll('select').forEach((node) => wrapSelect(node as HTMLSelectElement));
}

let documentListenersBound = false;

export function installSearchableSelects(root: ParentNode = document): void {
  enhanceSearchableSelects(root);
  observer?.disconnect();
  observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node instanceof HTMLSelectElement) wrapSelect(node);
        else enhanceSearchableSelects(node);
      });
    }
  });
  const target = root instanceof Element || root instanceof Document ? root : document;
  observer.observe(target === document ? document.body : (target as Node), {
    childList: true,
    subtree: true,
  });

  if (documentListenersBound) return;
  documentListenersBound = true;
  document.addEventListener('mousedown', (event) => {
    if (!openPanel) return;
    const targetNode = event.target as Node | null;
    if (!targetNode) return;
    if (openPanel.contains(targetNode)) return;
    if (openSelect?.closest('.nx-combo')?.contains(targetNode)) return;
    closePanel();
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && openPanel) closePanel();
  });
  window.addEventListener(
    'resize',
    () => {
      if (!openPanel || !openSelect) return;
      const trigger = openSelect
        .closest('.nx-combo')
        ?.querySelector<HTMLElement>('.nx-combo-trigger');
      if (trigger) positionPanel(trigger, openPanel);
    },
    { passive: true },
  );
  window.addEventListener(
    'scroll',
    () => {
      if (!openPanel || !openSelect) return;
      const trigger = openSelect
        .closest('.nx-combo')
        ?.querySelector<HTMLElement>('.nx-combo-trigger');
      if (trigger) positionPanel(trigger, openPanel);
    },
    true,
  );
}
